import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArchiveRestore,
  BookOpen,
  History,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  X,
} from 'lucide-react';
import {
  moldGuideService,
  MoldGuidePage,
  MoldGuidePageDetail,
  MoldGuidePageVersion,
  MoldGuidePageVersionDetail,
} from '../../services/api';
import { formatShortThaiDateFromString } from '../../utils/dateUtils';
import GuideEditor from './GuideEditor';
import {
  EMPTY_GUIDE_DOC,
  GUIDE_TYPOGRAPHY_CLASS,
  renderGuideHtml,
} from './guideContent';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';

interface MoldGuidesProps {
  moldId: string;
}

type GuideMode = 'view' | 'edit' | 'create';

interface GuideDraft {
  title: string;
  content: unknown;
  savedAt: string;
}

const draftKey = (moldId: string, pageId: number) =>
  `moldGuideDraft:${moldId}:${pageId}`;

const readDraft = (moldId: string, pageId: number): GuideDraft | null => {
  try {
    const raw = window.localStorage.getItem(draftKey(moldId, pageId));
    return raw ? (JSON.parse(raw) as GuideDraft) : null;
  } catch {
    return null;
  }
};

const writeDraft = (moldId: string, pageId: number, draft: GuideDraft) => {
  try {
    window.localStorage.setItem(
      draftKey(moldId, pageId),
      JSON.stringify(draft),
    );
  } catch {
    // Best-effort backup only; the editor still holds the content.
  }
};

const clearDraft = (moldId: string, pageId: number) => {
  try {
    window.localStorage.removeItem(draftKey(moldId, pageId));
  } catch {
    // Ignore.
  }
};

const guideErrorMessage = (error: unknown, fallback: string) => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 403) return 'ไม่มีสิทธิ์แก้ไขคู่มือแม่พิมพ์';
  if (status === 404) return 'ไม่พบหน้านี้ อาจถูกลบไปแล้ว กรุณาโหลดใหม่';
  return fallback;
};

const conflictHead = (error: unknown): MoldGuidePageDetail | null => {
  const response = (
    error as {
      response?: { status?: number; data?: { current?: MoldGuidePageDetail } };
    }
  )?.response;
  if (response?.status !== 409) return null;
  return response.data?.current ?? null;
};

const useMoldGuidePages = (moldId: string) => {
  const [pages, setPages] = useState<MoldGuidePage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const reload = useCallback(async () => {
    if (!Number(moldId)) return;
    setError(false);
    try {
      const result = await moldGuideService.list(moldId);
      setPages(result.data || []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [moldId]);
  useEffect(() => {
    const load = async () => reload();
    void load();
  }, [reload]);
  return { pages, loading, error, reload };
};

const pageMeta = (page: MoldGuidePage) => {
  const parts: string[] = [];
  if (page.updatedAt) {
    parts.push(formatShortThaiDateFromString(page.updatedAt));
  }
  if (page.updatedByName) {
    parts.push(`โดย ${page.updatedByName}`);
  }
  return parts.join(' · ');
};

const GuideContent = ({ content }: { content: unknown }) => (
  <div
    className={GUIDE_TYPOGRAPHY_CLASS}
    dangerouslySetInnerHTML={{ __html: renderGuideHtml(content) }}
  />
);

interface HistoryDialogProps {
  moldId: string;
  page: MoldGuidePageDetail;
  onClose: () => void;
  onRestored: (detail: MoldGuidePageDetail) => void;
  onError: (message: string) => void;
}

const HistoryDialog = ({
  moldId,
  page,
  onClose,
  onRestored,
  onError,
}: HistoryDialogProps) => {
  const [versions, setVersions] = useState<MoldGuidePageVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<MoldGuidePageVersionDetail | null>(
    null,
  );
  const [busyVersion, setBusyVersion] = useState<number | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await moldGuideService.listVersions(moldId, page.id);
        setVersions(result.data || []);
      } catch {
        onError('โหลดประวัติการแก้ไขไม่สำเร็จ');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [moldId, page.id, onError]);

  const showVersion = async (versionNo: number) => {
    setBusyVersion(versionNo);
    try {
      const result = await moldGuideService.getVersion(
        moldId,
        page.id,
        versionNo,
      );
      setPreview(result.data);
    } catch {
      onError('โหลดเวอร์ชันไม่สำเร็จ');
    } finally {
      setBusyVersion(null);
    }
  };

  const restoreVersion = async (versionNo: number) => {
    setBusyVersion(versionNo);
    try {
      const result = await moldGuideService.restore(moldId, page.id, versionNo);
      onRestored(result.data);
    } catch (error) {
      onError(guideErrorMessage(error, 'กู้คืนเวอร์ชันไม่สำเร็จ'));
      setBusyVersion(null);
    }
  };

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4'>
      <div className='flex max-h-[85vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl'>
        <div className='flex items-center justify-between border-b border-gray-200 px-6 py-4'>
          <h2 className='text-xl font-semibold'>ประวัติการแก้ไข</h2>
          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600 focus:outline-none'
            aria-label='ปิด'
          >
            <X size={24} />
          </button>
        </div>
        <div className='flex-1 overflow-y-auto px-6 py-4'>
          {loading ? (
            <div className='flex justify-center py-8 text-gray-400'>
              <Loader2 className='animate-spin' />
            </div>
          ) : (
            <ul className='divide-y divide-gray-100'>
              {versions.map((version) => {
                const isHead = version.versionNo === page.currentVersionNo;
                return (
                  <li key={version.id} className='py-3'>
                    <div className='flex flex-wrap items-center gap-2'>
                      <span className='inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600'>
                        v{version.versionNo}
                      </span>
                      {isHead && (
                        <span className='inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700'>
                          ปัจจุบัน
                        </span>
                      )}
                      <span className='min-w-0 flex-1 truncate text-sm text-gray-800'>
                        {version.title}
                      </span>
                      <span className='text-xs text-gray-500'>
                        {version.createdAt
                          ? formatShortThaiDateFromString(version.createdAt)
                          : ''}
                        {version.createdByName
                          ? ` · โดย ${version.createdByName}`
                          : ''}
                      </span>
                      <button
                        type='button'
                        onClick={() => showVersion(version.versionNo)}
                        disabled={busyVersion !== null}
                        className='rounded-md border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50'
                      >
                        ดู
                      </button>
                      {!isHead && (
                        <button
                          type='button'
                          onClick={() => restoreVersion(version.versionNo)}
                          disabled={busyVersion !== null}
                          className='inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50'
                        >
                          {busyVersion === version.versionNo ? (
                            <Loader2 size={12} className='animate-spin' />
                          ) : (
                            <RotateCcw size={12} />
                          )}
                          กู้คืนเวอร์ชันนี้
                        </button>
                      )}
                    </div>
                    {preview?.versionNo === version.versionNo && (
                      <div className='mt-3 rounded-md border border-gray-200 bg-gray-50 p-3'>
                        <GuideContent content={preview.content} />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

interface TrashDialogProps {
  moldId: string;
  onClose: () => void;
  onRestored: (detail: MoldGuidePageDetail) => void;
  onError: (message: string) => void;
}

// Deleted pages are only hidden, never destroyed; this dialog restores them.
const TrashDialog = ({
  moldId,
  onClose,
  onRestored,
  onError,
}: TrashDialogProps) => {
  const [deletedPages, setDeletedPages] = useState<MoldGuidePage[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyPage, setBusyPage] = useState<number | null>(null);
  const [preview, setPreview] = useState<MoldGuidePageDetail | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await moldGuideService.listDeleted(moldId);
        setDeletedPages(result.data || []);
      } catch {
        onError('โหลดรายการหน้าที่ถูกลบไม่สำเร็จ');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [moldId, onError]);

  const undeletePage = async (pageId: number) => {
    setBusyPage(pageId);
    try {
      const result = await moldGuideService.undelete(moldId, pageId);
      onRestored(result.data);
    } catch (error) {
      onError(guideErrorMessage(error, 'กู้คืนหน้าไม่สำเร็จ'));
      setBusyPage(null);
    }
  };

  const showPage = async (pageId: number) => {
    if (preview?.id === pageId) {
      setPreview(null);
      return;
    }
    setBusyPage(pageId);
    try {
      const result = await moldGuideService.getDeleted(moldId, pageId);
      setPreview(result.data);
    } catch (error) {
      onError(guideErrorMessage(error, 'โหลดหน้าไม่สำเร็จ'));
    } finally {
      setBusyPage(null);
    }
  };

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4'>
      <div className='flex max-h-[85vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl'>
        <div className='flex items-center justify-between border-b border-gray-200 px-6 py-4'>
          <h2 className='text-xl font-semibold'>หน้าที่ถูกลบ</h2>
          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600 focus:outline-none'
            aria-label='ปิด'
          >
            <X size={24} />
          </button>
        </div>
        <div className='flex-1 overflow-y-auto px-6 py-4'>
          {loading ? (
            <div className='flex justify-center py-8 text-gray-400'>
              <Loader2 className='animate-spin' />
            </div>
          ) : deletedPages.length === 0 ? (
            <p className='py-8 text-center text-sm text-gray-500'>
              ไม่มีหน้าที่ถูกลบ
            </p>
          ) : (
            <ul className='divide-y divide-gray-100'>
              {deletedPages.map((page) => (
                <li key={page.id} className='py-3'>
                  <div className='flex flex-wrap items-center gap-2'>
                    <div className='min-w-0 flex-1'>
                      <p className='truncate text-sm font-medium text-gray-800'>
                        {page.title}
                      </p>
                      <p className='mt-0.5 text-xs text-gray-500'>
                        ลบเมื่อ{' '}
                        {page.deletedAt
                          ? formatShortThaiDateFromString(page.deletedAt)
                          : '-'}
                        {page.deletedByName
                          ? ` · โดย ${page.deletedByName}`
                          : ''}
                      </p>
                    </div>
                    <button
                      type='button'
                      onClick={() => showPage(page.id)}
                      disabled={busyPage !== null}
                      className='rounded-md border border-gray-300 px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50'
                    >
                      {preview?.id === page.id ? 'ซ่อน' : 'ดู'}
                    </button>
                    <button
                      type='button'
                      onClick={() => undeletePage(page.id)}
                      disabled={busyPage !== null}
                      className='inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50'
                    >
                      {busyPage === page.id ? (
                        <Loader2 size={14} className='animate-spin' />
                      ) : (
                        <ArchiveRestore size={14} />
                      )}
                      กู้คืน
                    </button>
                  </div>
                  {preview?.id === page.id && (
                    <div className='mt-3 rounded-md border border-gray-200 bg-gray-50 p-3'>
                      <GuideContent content={preview.content} />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const MoldGuides = ({ moldId }: MoldGuidesProps) => {
  const { pages, loading, error, reload } = useMoldGuidePages(moldId);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<MoldGuidePageDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [mode, setMode] = useState<GuideMode>('view');
  const [titleInput, setTitleInput] = useState('');
  const [baseVersion, setBaseVersion] = useState(1);
  const [conflict, setConflict] = useState<MoldGuidePageDetail | null>(null);
  const [showLatest, setShowLatest] = useState(false);
  const [storedDraft, setStoredDraft] = useState<GuideDraft | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  // The document the editor (re)mounts with; docRef tracks live edits.
  const [initialDoc, setInitialDoc] = useState<unknown>(EMPTY_GUIDE_DOC);
  const [saving, setSaving] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MoldGuidePage | null>(null);
  const docRef = useRef<unknown>(EMPTY_GUIDE_DOC);

  const effectiveSelectedId =
    mode === 'create' ? null : (selectedId ?? pages[0]?.id ?? null);

  useEffect(() => {
    if (!effectiveSelectedId || !Number(moldId)) {
      return;
    }
    let cancelled = false;
    const load = async () => {
      setDetailLoading(true);
      try {
        const result = await moldGuideService.get(moldId, effectiveSelectedId);
        if (!cancelled) setDetail(result.data);
      } catch {
        if (!cancelled) setDetail(null);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [moldId, effectiveSelectedId]);

  const selectPage = (pageId: number) => {
    setSelectedId(pageId);
    setMode('view');
    setConflict(null);
    setMutationError(null);
  };

  const startCreate = () => {
    setMode('create');
    setTitleInput('');
    docRef.current = EMPTY_GUIDE_DOC;
    setInitialDoc(EMPTY_GUIDE_DOC);
    setConflict(null);
    setStoredDraft(null);
    setMutationError(null);
    setEditorKey((key) => key + 1);
  };

  const startEdit = () => {
    if (!detail) return;
    setTitleInput(detail.title);
    docRef.current = detail.content;
    setInitialDoc(detail.content);
    setBaseVersion(detail.currentVersionNo);
    setConflict(null);
    setShowLatest(false);
    setStoredDraft(readDraft(moldId, detail.id));
    setMutationError(null);
    setEditorKey((key) => key + 1);
    setMode('edit');
  };

  const cancelEditing = () => {
    setMode('view');
    setConflict(null);
    setMutationError(null);
  };

  const applyStoredDraft = () => {
    if (!storedDraft) return;
    setTitleInput(storedDraft.title);
    docRef.current = storedDraft.content;
    setInitialDoc(storedDraft.content);
    setEditorKey((key) => key + 1);
    setStoredDraft(null);
  };

  const discardStoredDraft = () => {
    if (detail) clearDraft(moldId, detail.id);
    setStoredDraft(null);
  };

  const confirmSave = async () => {
    const title = titleInput.trim();
    if (!title) {
      setMutationError('กรุณาตั้งชื่อหน้า');
      return;
    }
    setSaving(true);
    setMutationError(null);
    try {
      if (mode === 'create') {
        const result = await moldGuideService.create(moldId, {
          title,
          content: docRef.current,
        });
        setSelectedId(result.data.id);
        setDetail(result.data);
      } else {
        if (!detail) return;
        const result = await moldGuideService.save(moldId, detail.id, {
          title,
          content: docRef.current,
          baseVersion,
        });
        clearDraft(moldId, detail.id);
        setDetail(result.data);
      }
      setMode('view');
      setConflict(null);
      await reload();
    } catch (mutationErr) {
      const head = conflictHead(mutationErr);
      if (head && detail) {
        // Someone saved first. Keep this user's editing untouched, back it up
        // locally, and rebase the next save onto the new head.
        writeDraft(moldId, detail.id, {
          title,
          content: docRef.current,
          savedAt: new Date().toISOString(),
        });
        setConflict(head);
        setBaseVersion(head.currentVersionNo);
      } else {
        setMutationError(
          guideErrorMessage(mutationErr, 'บันทึกไม่สำเร็จ กรุณาลองอีกครั้ง'),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      // Soft delete: keep any local draft around in case the page is restored.
      await moldGuideService.remove(moldId, deleteTarget.id);
      if (effectiveSelectedId === deleteTarget.id) {
        setSelectedId(null);
        setDetail(null);
        setMode('view');
      }
      await reload();
    } catch (deleteErr) {
      setMutationError(guideErrorMessage(deleteErr, 'ลบหน้าไม่สำเร็จ'));
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleRestored = async (restored: MoldGuidePageDetail) => {
    setDetail(restored);
    setHistoryOpen(false);
    setMode('view');
    await reload();
  };

  const handleUndeleted = async (restored: MoldGuidePageDetail) => {
    setTrashOpen(false);
    setSelectedId(restored.id);
    setDetail(restored);
    setMode('view');
    await reload();
  };

  const editing = mode === 'edit' || mode === 'create';

  if (loading) {
    return (
      <div className='rounded-lg bg-white p-6 shadow'>
        <div className='flex justify-center py-8 text-gray-400'>
          <Loader2 className='animate-spin' />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='rounded-lg bg-white p-6 shadow'>
        <p className='py-8 text-center text-sm text-gray-500'>
          โหลดคู่มือไม่สำเร็จ กรุณาลองใหม่อีกครั้ง
        </p>
      </div>
    );
  }

  return (
    <div className='rounded-lg bg-white p-4 shadow sm:p-6'>
      <div className='mb-4 flex flex-wrap items-center justify-between gap-3'>
        <h3 className='flex items-center gap-2 text-lg font-semibold text-gray-900'>
          <BookOpen size={20} className='text-gray-400' />
          คู่มือ & เทคนิค
          {pages.length > 0 && (
            <span className='inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600'>
              {pages.length}
            </span>
          )}
        </h3>
        <div className='flex items-center gap-2'>
          <button
            type='button'
            onClick={() => setTrashOpen(true)}
            className='inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
          >
            <ArchiveRestore size={16} />
            หน้าที่ถูกลบ
          </button>
          <button
            type='button'
            onClick={startCreate}
            className='inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
          >
            <Plus size={16} />
            สร้างหน้าใหม่
          </button>
        </div>
      </div>

      {mutationError && mode !== 'edit' && mode !== 'create' && (
        <div className='mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700'>
          {mutationError}
        </div>
      )}

      {pages.length === 0 && mode !== 'create' ? (
        <div className='rounded-lg border border-dashed border-gray-300 py-12 text-center'>
          <BookOpen size={32} className='mx-auto mb-3 text-gray-300' />
          <p className='mb-1 text-sm font-medium text-gray-700'>
            ยังไม่มีคู่มือสำหรับแม่พิมพ์นี้
          </p>
          <p className='mb-4 text-sm text-gray-500'>
            รวบรวมวิธีซ่อม จุดที่ต้องระวัง และเทคนิคของแม่พิมพ์นี้ไว้ให้ทีม
          </p>
          <button
            type='button'
            onClick={startCreate}
            className='inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700'
          >
            <Plus size={16} />
            สร้างหน้าแรก
          </button>
        </div>
      ) : (
        <div className='flex flex-col gap-4 md:flex-row'>
          {pages.length > 0 && (
            <aside className='md:w-60 md:shrink-0'>
              <ul className='flex gap-2 overflow-x-auto md:flex-col md:overflow-visible'>
                {pages.map((page) => {
                  const active =
                    mode !== 'create' && page.id === effectiveSelectedId;
                  return (
                    <li key={page.id} className='min-w-[12rem] md:min-w-0'>
                      <button
                        type='button'
                        onClick={() => selectPage(page.id)}
                        className={`w-full rounded-md border px-3 py-2 text-left transition ${
                          active
                            ? 'border-blue-200 bg-blue-50'
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                        }`}
                      >
                        <span
                          className={`block truncate text-sm font-medium ${
                            active ? 'text-blue-700' : 'text-gray-800'
                          }`}
                        >
                          {page.title}
                        </span>
                        <span className='mt-0.5 block truncate text-xs text-gray-500'>
                          {pageMeta(page)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>
          )}

          <div className='min-w-0 flex-1'>
            {editing ? (
              <div>
                {conflict && (
                  <div className='mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800'>
                    <p>
                      มีผู้อื่นบันทึกหน้านี้ไปก่อนหน้า
                      {conflict.updatedByName
                        ? ` (โดย ${conflict.updatedByName})`
                        : ''}{' '}
                      การแก้ไขของคุณยังอยู่ในหน้าจอและถูกสำรองไว้ในเครื่องแล้ว
                      โปรดเทียบกับเวอร์ชันล่าสุดก่อนบันทึกอีกครั้ง
                    </p>
                    <button
                      type='button'
                      onClick={() => setShowLatest((value) => !value)}
                      className='mt-2 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100'
                    >
                      {showLatest ? 'ซ่อนเวอร์ชันล่าสุด' : 'ดูเวอร์ชันล่าสุด'}
                    </button>
                    {showLatest && (
                      <div className='mt-3 rounded-md border border-amber-200 bg-white p-3'>
                        <GuideContent content={conflict.content} />
                      </div>
                    )}
                  </div>
                )}
                {storedDraft && !conflict && (
                  <div className='mb-3 flex flex-wrap items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800'>
                    <span className='flex-1'>
                      พบฉบับร่างที่ยังไม่ได้บันทึกของหน้านี้
                    </span>
                    <button
                      type='button'
                      onClick={applyStoredDraft}
                      className='rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700'
                    >
                      ใช้ฉบับร่าง
                    </button>
                    <button
                      type='button'
                      onClick={discardStoredDraft}
                      className='rounded-md border border-blue-300 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100'
                    >
                      ทิ้งฉบับร่าง
                    </button>
                  </div>
                )}
                <label
                  htmlFor='guide-page-title'
                  className='mb-1 block text-sm font-medium text-gray-700'
                >
                  ชื่อหน้า
                </label>
                <input
                  id='guide-page-title'
                  type='text'
                  value={titleInput}
                  onChange={(event) => setTitleInput(event.target.value)}
                  placeholder='เช่น วิธีซ่อม hot runner'
                  className='mb-3 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
                />
                <GuideEditor
                  key={editorKey}
                  initialContent={initialDoc}
                  onDocChange={(doc) => {
                    docRef.current = doc;
                  }}
                />
                {mutationError && (
                  <div className='mt-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700'>
                    {mutationError}
                  </div>
                )}
                <div className='mt-4 flex justify-end gap-2'>
                  <button
                    type='button'
                    onClick={cancelEditing}
                    className='rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50'
                  >
                    ยกเลิก
                  </button>
                  <button
                    type='button'
                    onClick={confirmSave}
                    disabled={saving}
                    className='inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60'
                  >
                    {saving && <Loader2 size={16} className='animate-spin' />}
                    บันทึก
                  </button>
                </div>
              </div>
            ) : detailLoading ? (
              <div className='flex justify-center py-12 text-gray-400'>
                <Loader2 className='animate-spin' />
              </div>
            ) : detail ? (
              <article>
                <div className='mb-3 flex flex-wrap items-start justify-between gap-2 border-b border-gray-100 pb-3'>
                  <div className='min-w-0'>
                    <h4 className='truncate text-lg font-semibold text-gray-900'>
                      {detail.title}
                    </h4>
                    <p className='mt-0.5 text-xs text-gray-500'>
                      เวอร์ชัน {detail.currentVersionNo}
                      {detail.updatedAt
                        ? ` · แก้ไขล่าสุด ${formatShortThaiDateFromString(detail.updatedAt)}`
                        : ''}
                      {detail.updatedByName
                        ? ` โดย ${detail.updatedByName}`
                        : ''}
                    </p>
                  </div>
                  <div className='flex items-center gap-1'>
                    <button
                      type='button'
                      onClick={startEdit}
                      aria-label='แก้ไขหน้า'
                      title='แก้ไขหน้า'
                      className='rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      type='button'
                      onClick={() => setHistoryOpen(true)}
                      aria-label='ประวัติการแก้ไข'
                      title='ประวัติการแก้ไข'
                      className='rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                    >
                      <History size={16} />
                    </button>
                    <button
                      type='button'
                      onClick={() => setDeleteTarget(detail)}
                      aria-label='ลบหน้า'
                      title='ลบหน้า'
                      className='rounded-md p-2 text-gray-500 hover:bg-red-50 hover:text-red-600'
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <GuideContent content={detail.content} />
              </article>
            ) : (
              <p className='py-12 text-center text-sm text-gray-500'>
                เลือกหน้าจากรายการเพื่อดูเนื้อหา
              </p>
            )}
          </div>
        </div>
      )}

      {trashOpen && (
        <TrashDialog
          moldId={moldId}
          onClose={() => setTrashOpen(false)}
          onRestored={handleUndeleted}
          onError={(message) => setMutationError(message)}
        />
      )}

      {historyOpen && detail && (
        <HistoryDialog
          moldId={moldId}
          page={detail}
          onClose={() => setHistoryOpen(false)}
          onRestored={handleRestored}
          onError={(message) => setMutationError(message)}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmationDialog
          setShowDeleteConfirm={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
          text={`ต้องการลบหน้า "${deleteTarget.title}" หรือไม่? หน้าจะถูกซ่อนจากรายการ และสามารถกู้คืนได้จาก "หน้าที่ถูกลบ"`}
          confirmText='ลบหน้า'
        />
      )}
    </div>
  );
};

export default MoldGuides;
